/** Variant-specific restock registration. No marketing signup or local email storage. */
export function confirmedReceipt(data, selection, nonce) {
  return data?.ok === true && data.status === 'queued' &&
    /^[a-f0-9-]{36}$/.test(data.receipt || '') && data.nonce === nonce &&
    String(data.variantId) === String(selection.variantId) &&
    String(data.productId) === String(selection.productId);
}

export function bridgeUrl(endpoint, selection, nonce, origin) {
  if (!/^https:\/\/script\.google\.com\/macros\/s\/[a-zA-Z0-9_-]+\/exec$/.test(endpoint || '')) throw new Error('service');
  if (!Number.isSafeInteger(Number(selection.variantId)) || !Number.isSafeInteger(Number(selection.productId))) throw new Error('error');
  const path = new URL(selection.url,origin).pathname;
  const handle = path.match(/\/products\/([a-z0-9-]+)$/)?.[1];
  if (!handle) throw new Error('error');
  const url = new URL(endpoint);
  url.search = new URLSearchParams({origin,nonce,handle,productId:String(selection.productId),variantId:String(selection.variantId)});
  return url.href;
}

function frameContains(frame, source, depth = 0) {
  if (frame === source) return true;
  if (depth > 3) return false;
  try {for(let i=0;i<frame.frames.length;i++)if(frameContains(frame.frames[i],source,depth+1))return true;} catch {return false;}
  return false;
}

export async function registerRestock(selection, email, context, signal) {
  if (!context.enabled || context.provider !== 'google') throw new Error('service');
  const nonce = Array.from(crypto.getRandomValues(new Uint8Array(16)),byte=>byte.toString(16).padStart(2,'0')).join('');
  const url = bridgeUrl(context.endpoint,selection,nonce,window.location.origin);
  return new Promise((resolve,reject) => {
    const frame=document.createElement('iframe');
    frame.hidden=true;frame.setAttribute('aria-hidden','true');frame.referrerPolicy='no-referrer';frame.src=url;
    let source,sourceOrigin;
    const done = (error,result) => {clearTimeout(timeout);window.removeEventListener('message',onMessage);signal?.removeEventListener('abort',onAbort);frame.remove();error?reject(error):resolve(result);};
    const onAbort=()=>done(new Error('error'));
    const onMessage=event=>{
      if(!/^https:\/\/(?:script\.google\.com|[a-z0-9-]+-script\.googleusercontent\.com)$/.test(event.origin) || event.data?.nonce!==nonce || !frameContains(frame.contentWindow,event.source))return;
      if(event.data.type==='memys-restock:ready' && !source && String(event.data.variantId)===String(selection.variantId) && String(event.data.productId)===String(selection.productId)) {
        source=event.source;sourceOrigin=event.origin;
        source.postMessage({type:'memys-restock:register',nonce,email:email.trim(),honey:context.honey || ''},sourceOrigin);
      } else if(event.data.type==='memys-restock:result' && event.source===source && event.origin===sourceOrigin) {
        const result=event.data.result;
        if(confirmedReceipt(result,selection,nonce))done(null,result);
        else done(new Error(result?.status==='available'?'available':'error'));
      }
    };
    const timeout=setTimeout(()=>done(new Error('error')),35000);
    if(signal?.aborted){done(new Error('error'));return;}
    window.addEventListener('message',onMessage);signal?.addEventListener('abort',onAbort,{once:true});
    document.body.append(frame);
  });
}

if (typeof document !== 'undefined') {
  const dialog = document.getElementById('MemysRestockDialog');
  if (dialog) {
    const labels = JSON.parse(dialog.querySelector('[data-restock-labels]').textContent);
    const form = dialog.querySelector('[data-restock-form]');
    const input = dialog.querySelector('input[name="email"]');
    const submit = dialog.querySelector('[data-restock-submit]');
    const feedback = dialog.querySelector('[data-restock-feedback]');
    const productLink = dialog.querySelector('[data-restock-product-link]');
    let selected, trigger, pending = false, controller, generation = 0;

    function showFeedback(message, success = false) {
      feedback.replaceChildren();
      feedback.dataset.state = success ? 'success' : 'error';
      if (success) {
        const heading = document.createElement('strong');
        heading.textContent = labels.success;
        feedback.append(heading,document.createTextNode(labels.successDetail));
      } else feedback.textContent = message;
      feedback.hidden = false;
    }

    function open(selection, opener) {
      if (!selection?.variantId || selection.available || dialog.open) return;
      generation++; selected = {...selection}; trigger = opener;
      form.reset(); form.hidden = false; feedback.hidden = true; productLink.hidden = true;
      submit.disabled = false; submit.textContent = labels.submit;
      dialog.querySelector('[data-restock-title]').textContent = selected.title;
      dialog.querySelector('[data-restock-format]').textContent = selected.formatLabel;
      const image = dialog.querySelector('[data-restock-image]');
      if (selected.image) { image.src = selected.image; image.hidden = false; } else image.hidden = true;
      productLink.href = selected.url+'?variant='+encodeURIComponent(selected.variantId);
      dialog.showModal();
    }

    dialog.querySelector('[data-restock-close]').addEventListener('click',()=>dialog.close());
    dialog.addEventListener('click',event=>{if(event.target===dialog){const r=dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)dialog.close();}});
    dialog.addEventListener('close',()=>{
      generation++; controller?.abort(); pending=false; form.reset(); selected=null;
      if (trigger?.isConnected) trigger.focus({preventScroll:true});
    });

    form.addEventListener('submit',async event=>{
      event.preventDefault(); event.stopPropagation();
      if (pending || !selected || !form.reportValidity()) return;
      const context = {provider:dialog.dataset.provider,enabled:dialog.dataset.enabled==='true',endpoint:dialog.dataset.endpoint,honey:form.elements.website.value};
      if (!context.enabled || context.provider==='none') {showFeedback(labels.unavailableService);return;}
      const selection = {...selected};
      const currentGeneration = generation;
      pending=true; submit.disabled=true; submit.textContent=labels.sending; feedback.hidden=true;
      controller=new AbortController(); const requestController=controller;
      const timeout=setTimeout(()=>requestController.abort(),45000);
      try {
        let product;
        try {
          const url = new URL(selection.url+'.js',window.location.origin);
          const response=await fetch(url,{cache:'no-store',credentials:'same-origin',signal:requestController.signal});
          if(!response.ok)throw new Error();
          product=await response.json();
        } catch {throw new Error('check');}
        const variant=product.variants?.find(v=>String(v.id)===String(selection.variantId));
        if(!variant)throw new Error('check');
        if(generation!==currentGeneration || !dialog.open)return;
        if(variant.available){
          showFeedback(labels.available); form.hidden=true; productLink.hidden=false;
          return;
        }
        await registerRestock(selection,input.value,context,requestController.signal);
        if(dialog.open && generation===currentGeneration && String(selected?.variantId)===String(selection.variantId)){
          form.hidden=true; showFeedback('',true);
        }
      } catch(error) {
        if(dialog.open && generation===currentGeneration){
          if(error.message==='available'){showFeedback(labels.available);form.hidden=true;productLink.hidden=false;}
          else showFeedback(error.message==='check'?labels.checkError:error.message==='service'?labels.unavailableService:labels.error);
        }
      } finally {
        clearTimeout(timeout);
        if(controller===requestController && generation===currentGeneration){pending=false;submit.disabled=false;submit.textContent=labels.submit;}
      }
    });

    class MemysRestockEntry extends HTMLElement {
      connectedCallback() {
        if(this._abort)return;
        this._abort=new AbortController();
        this.addEventListener('click',event=>{
          if(!event.target.closest('[data-restock-open]'))return;
          event.preventDefault();event.stopPropagation();
          const selection=JSON.parse(this.querySelector('[data-restock-product]').textContent);
          open(selection,this.querySelector('[data-restock-open]'));
        },{signal:this._abort.signal});
        const scope=this.closest('product-card') || this.closest('.shopify-section');
        scope?.addEventListener('variant:update',event=>{
          const detail=event.detail;
          if(String(detail?.data?.productId)!==this.dataset.productId&&!detail?.data?.newProduct)return;
          const variant=detail.resource;
          this.hidden=!variant || variant.available;
          const old=JSON.parse(this.querySelector('[data-restock-product]').textContent);
          const next=Array.from(detail.data.html?.querySelectorAll('memys-restock-entry')||[]).find(entry=>entry.dataset.context===this.dataset.context&&entry.dataset.productId===String(detail.data.newProduct?.id||detail.data.productId));
          if(next){
            this.dataset.productId=next.dataset.productId;
            this.querySelector('[data-restock-product]').textContent=next.querySelector('[data-restock-product]').textContent;
          } else if(variant){
            old.variantId=variant.id;old.available=variant.available;old.variantTitle=variant.title;
            if(variant.featured_media?.preview_image?.src)old.image=variant.featured_media.preview_image.src;
            this.querySelector('[data-restock-product]').textContent=JSON.stringify(old);
          }
          if(dialog.open&&String(selected?.productId)===this.dataset.productId&&String(selected.variantId)!==String(variant?.id))dialog.close();
        },{signal:this._abort.signal});
      }
      disconnectedCallback(){this._abort?.abort();this._abort=null;}
    }
    if(!customElements.get('memys-restock-entry'))customElements.define('memys-restock-entry',MemysRestockEntry);
  }
}
